'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { FC } from 'react';
import { PRICE_INCLUDES_VAT_CONFIG_KEY } from '@/constants/system-config-keys';
import useSystemConfig from '@/hooks/use-system-config';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { cn } from '@/utils';
import { getSystemConfigString } from '@/utils/system-config';

const vatSuffix = cva('shrink-0 font-normal text-default-700 bg-red-100 px-3 py-1 rounded-full', {
  variants: {
    size: {
      xs: 'text-xxs',
      sm: 'text-xxs',
      md: 'text-xxs',
      lg: 'text-xs',
      xl: 'text-xs',
      '2xl': 'text-xs',
      '3xl': 'text-sm',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export type VatPriceSuffixSize = NonNullable<VariantProps<typeof vatSuffix>['size']>;

type Props = {
  size: VatPriceSuffixSize;
  className?: string;
  customColor?: string;
  customBackground?: string;
};

const VatPriceSuffix: FC<Props> = ({ size, className, customColor, customBackground }) => {
  const { t } = useTranslation();
  const systemConfig = useSystemConfig();
  const priceIncludesVat = getSystemConfigString(systemConfig, PRICE_INCLUDES_VAT_CONFIG_KEY);
  // Default-on policy: only suppress the "without VAT" badge when the realm
  // EXPLICITLY says prices already include VAT (`priceIncludesVat === 'true'`).
  // The previous gate (`priceIncludesVat !== 'false'`) hid the badge whenever
  // the backend returned anything other than the literal string `'false'` —
  // including `undefined` (key not seeded on the realm), `null`, or a network
  // error — which silently broke the badge across the whole storefront for any
  // tenant that hadn't configured the key.

  if (priceIncludesVat === 'true') {
    return null;
  }

  return (
    <span
      className={cn(vatSuffix({ size }), className)}
      style={{
        ...(customColor ? { color: customColor } : {}),
        ...(customBackground ? { backgroundColor: customBackground } : {}),
      }}
    >
      {t?.common.priceWithoutVat ?? 'without VAT'}
    </span>
  );
};

export default VatPriceSuffix;
